import React, { useEffect, useRef } from 'react';
import { uniqueId } from 'lodash-es';
import HBFlex from '../HBFlex/HBFlex';
import HBText from '../HBText/HBText';
import HBInfoIcon from '../HBInfoIcon/HBInfoIcon';
import {
    CAPTION_TEXT_TYPE,
    FieldContainer,
    LABEL_TEXT_TYPE,
    LabelCounterWrapper,
    LabelStack,
    MESSAGE_TEXT_TYPE,
    MessageArea,
    MessageLiveRegion,
    MessageText,
    StyledLabel
} from './HBFieldControl.styles';
import {
    HBFieldControlProps,
    HBFieldControlRenderProps
} from './HBFieldControl.types';

/**
 * Generic field wrapper: renders the label, subLabel, caption, character counter,
 * and hint/error/disabled message around any control, and hands the control its
 * wired props (id, aria, size, error…) through the `render` slot.
 *
 * Works both standalone (controlled) and form-bound (spread a form-binding bundle
 * onto it). Chrome styling matches the HB*Field components.
 */
const HBFieldControl = (props: HBFieldControlProps) => {
    const {
        id: idProp,
        label,
        ariaLabel,
        subLabel,
        caption,
        placement = 'top',
        reserveLabelSpace = true,
        reserveMessageSpace = false,
        messageLayout = 'beside',
        margin,
        size,
        labelSize = size ?? 'sm',
        labelAlignment = 'center',
        required,
        disabled,
        error,
        errorMessage,
        hintMessage,
        disabledMessage,
        showHintMessage = false,
        tooltip,
        tooltipAriaLabel = 'More information',
        tooltipIcon,
        tooltipIconColor,
        tooltipIconHoverColor,
        tooltipKeepOpenOnHover,
        tooltipEnableInitialOpen,
        tooltipShouldSupportClickOutside,
        onTooltipOpen,
        width = '100%',
        className,
        name,
        value,
        onChange,
        onBlur,
        maxLength,
        render
    } = props;

    const autoId = useRef(uniqueId('hb-field-')).current;
    const id = idProp ?? autoId;
    const messageId = `${id}-message`;
    const messageRegionId = `${id}-message-region`;
    const labelId = label ? `${id}-label` : undefined;
    const subLabelId = subLabel ? `${id}-sublabel` : undefined;
    const captionId = caption ? `${id}-caption` : undefined;

    const message = disabled
        ? disabledMessage
        : error
        ? errorMessage
        : hintMessage;
    const hasMessage = !!message;
    // Declared here rather than beside the message block below: the label column
    // needs it too, to mirror the message area's height at its foot (see
    // LabelCounterWrapper), and that block is built earlier.
    const isMessageVisible =
        (!!error && !!errorMessage) ||
        (!!disabled && !!disabledMessage) ||
        (showHintMessage && !!hintMessage);

    const controlBundle: HBFieldControlRenderProps = {
        id,
        name,
        value,
        onChange,
        onBlur,
        size,
        required,
        disabled,
        error,
        maxLength,
        'aria-labelledby': labelId,
        // subLabel/caption render outside the <label> (see LabelStack below),
        // so they aren't part of its accessible name — wire them here instead
        // of dropping them from the announcement entirely.
        'aria-describedby':
            [subLabelId, captionId, hasMessage && messageId]
                .filter(Boolean)
                .join(' ') || undefined,
        'aria-invalid': error || undefined,
        'aria-label': !label ? ariaLabel : undefined
    };

    const content = render(controlBundle);

    // Depend on presence, not identity: `label` is a ReactNode, so a JSX label
    // is a fresh object every render and would re-fire the warning below on
    // each one. Only whether a label exists matters here.
    const hasLabel = !!label;
    const hasAriaLabel = !!ariaLabel;

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            return;
        }
        if (!hasLabel && !hasAriaLabel) {
            // Fires for a `render` slot that deliberately ignores the bundle to
            // show non-control content (a read-only div), which has no name to
            // announce. That false positive is accepted: "did render() read the
            // bundle" is a different question from "is this a control" — a slot
            // can read a single key for non-control content, and can render a
            // nameless <input> without reading any. Detecting it properly means
            // inspecting the output for a focusable element, which is more
            // machinery than a dev-only warning justifies.
            // eslint-disable-next-line no-console -- dev-only log
            console.warn(
                'HBFieldControl: no accessible name — pass `label` (visible) or `ariaLabel` (accessible-name-only) so screen readers can announce this field. Safe to ignore when `render` returns non-control content.'
            );
        } else if (hasLabel && hasAriaLabel) {
            // eslint-disable-next-line no-console -- dev-only log
            console.warn(
                'HBFieldControl: `ariaLabel` is ignored when `label` is set — the visible label already provides the accessible name via its `<label htmlFor>` association.'
            );
        }
    }, [hasLabel, hasAriaLabel]);

    const labelColor = disabled ? 'text.disabled' : 'text.primary';
    const mutedColor = disabled ? 'text.disabled' : 'text.secondary';
    const isColumn = placement === 'top';
    // Top placement already puts the message on its own row, so `row` only
    // changes left/right placement.
    const isMessageRow = messageLayout === 'row' && !isColumn;
    const hasCounter =
        typeof maxLength === 'number' && typeof value === 'string';

    // In top placement, a labelless field sitting beside labeled siblings (e.g. an
    // icon-only search field in a row of labeled inputs) would otherwise render its
    // control flush at the top, misaligned with siblings whose control is pushed
    // down by their label row. This invisible placeholder reserves that same height
    // — built from the same LabelCounterWrapper + HBText type so it matches exactly
    // — without affecting left/right placement, where there's no equivalent "reserve
    // the width a label would have taken" concept. Opt out with
    // reserveLabelSpace={false} for a standalone labelless field where the
    // reserved space is just unwanted empty room.
    const hiddenLabelSpacer = isColumn &&
        !label &&
        !hasCounter &&
        reserveLabelSpace && (
            <LabelCounterWrapper $placement={placement} $hidden>
                {/* A literal empty string collapses this to 0 height — an inline
                element with no content gets no line box at all, regardless of
                font-size/line-height. The space character forces a real line
                box, which is what reserves the height. Do not "simplify"
                this to {''}. */}
                <HBText type={LABEL_TEXT_TYPE[labelSize]}>{' '}</HBText>
            </LabelCounterWrapper>
        );

    // Rendered whether or not `label` is set — an ariaLabel-only field (no visible
    // label) can still document itself with a tooltip.
    const tooltipIconElement = tooltip && (
        <HBInfoIcon
            tooltip={tooltip}
            ariaLabel={tooltipAriaLabel}
            icon={tooltipIcon}
            iconColor={tooltipIconColor}
            iconHoverColor={tooltipIconHoverColor}
            keepOpenOnHover={tooltipKeepOpenOnHover}
            tooltipEnableInitialOpen={tooltipEnableInitialOpen}
            tooltipShouldSupportClickOutside={tooltipShouldSupportClickOutside}
            onTooltipOpen={onTooltipOpen}
        />
    );

    const labelBlock = (label || hasCounter || tooltip) && (
        <LabelCounterWrapper
            $placement={placement}
            $labelAlignment={labelAlignment}
            $reserveMessageSpace={!isMessageRow && reserveMessageSpace}
            $hasMessage={!isMessageRow && hasMessage}
            $isMessageVisible={!isMessageRow && isMessageVisible}
        >
            <HBFlex
                // 'space-between' only belongs to top placement, where the label
                // block spans the field width and the counter is meant to sit at
                // the far edge. In left/right placement the block sits beside the
                // control — 'right' stretches it via flex: 1 (see
                // LABEL_PLACEMENT_PADDING) so a long label can wrap — and
                // spreading would strand the counter at the field edge, far from
                // the label it counts. Keep them adjacent on the `gap` instead.
                justify={
                    !label
                        ? 'flex-end'
                        : isColumn
                        ? 'space-between'
                        : 'flex-start'
                }
                align="flex-start"
                gap="4"
            >
                {label ? (
                    <LabelStack>
                        <HBFlex align="center" gap="4">
                            <StyledLabel
                                id={labelId}
                                htmlFor={id}
                                $clickable={!!id}
                                // `htmlFor` only auto-focuses labelable elements
                                // (input/select/textarea/button) — controls with
                                // an explicit ARIA widget role (HBSelect,
                                // HBCombobox-based controls) render a div, which
                                // `<label for>` cannot activate. This click
                                // handler makes the label's pointer cursor
                                // actually do something for those too.
                                onClick={() =>
                                    document.getElementById(id)?.focus()
                                }
                            >
                                <HBText
                                    type={LABEL_TEXT_TYPE[labelSize]}
                                    color={labelColor}
                                >
                                    {label}
                                    {required && ' *'}
                                </HBText>
                            </StyledLabel>
                            {tooltipIconElement}
                        </HBFlex>
                        {subLabel && (
                            <HBText
                                id={subLabelId}
                                type={LABEL_TEXT_TYPE[labelSize]}
                                color={mutedColor}
                            >
                                {subLabel}
                            </HBText>
                        )}
                        {caption && (
                            <HBText
                                id={captionId}
                                type={CAPTION_TEXT_TYPE[labelSize]}
                                color={mutedColor}
                            >
                                {caption}
                            </HBText>
                        )}
                    </LabelStack>
                ) : (
                    tooltipIconElement
                )}
                {hasCounter && typeof value === 'string' && (
                    <HBText
                        type={LABEL_TEXT_TYPE[labelSize]}
                        color={
                            value.length > (maxLength as number)
                                ? 'text.danger'
                                : 'text.secondary'
                        }
                    >
                        {value.length}/{maxLength}
                    </HBText>
                )}
            </HBFlex>
        </LabelCounterWrapper>
    );

    const messageColor = disabled
        ? 'text.disabled'
        : error
        ? 'text.danger'
        : 'text.secondary';

    // A hint is already read from `aria-describedby` when focus arrives, and CSS
    // `:focus-within` reveals it at that same moment — inside the live region that
    // reveal also registers as an addition, so the hint gets read twice. Only the
    // error and disabled messages, which report a state change the user needs to
    // hear about, are placed in the region. A hint renders as its sibling instead:
    // same id, so `aria-describedby` is unaffected, and an empty block either side
    // adds no height.
    const isLiveMessage = hasMessage && (!!disabled || !!error);

    const messageText = hasMessage && (
        <MessageText
            id={messageId}
            type={MESSAGE_TEXT_TYPE}
            color={messageColor}
            $isVisible={isMessageVisible}
        >
            {message}
        </MessageText>
    );

    // The region is mounted for the whole life of the field and only its contents
    // change, so an error replaced while the control keeps focus is announced
    // (HBUI-650): `aria-describedby` is read once, when focus arrives, and never
    // again. There is no opt-out — suppressing a message the field was given is
    // the gap HBUI-650 set out to close. `polite` rather than
    // `role="alert"`/`assertive` because validation that runs on change would
    // otherwise interrupt the user on every keystroke, and `aria-atomic` because
    // the message is replaced wholesale rather than appended to.
    const messageBlock = (
        <MessageArea
            $reserve={reserveMessageSpace}
            $hasMessage={hasMessage}
            $isVisible={isMessageVisible}
        >
            <MessageLiveRegion
                id={messageRegionId}
                aria-live="polite"
                aria-atomic="true"
            >
                {isLiveMessage && messageText}
            </MessageLiveRegion>
            {!isLiveMessage && messageText}
        </MessageArea>
    );

    // A column holding the control with its message directly beneath it, so the
    // message tracks the control rather than the row that also carries the label.
    // `flex` is the row placement: in 'right' the control renders before the
    // label, so growing here would push the label to the far edge of the field —
    // wrong for the checkbox/switch case that placement exists for. Let the
    // control size to its content and give the label the remaining width.
    const controlBlock = (
        <HBFlex
            displayName="ControlWrapper"
            direction="column"
            minWidth="0"
            flex={placement === 'right' ? '0 0 auto' : '1'}
        >
            {content}
            {!isMessageRow && messageBlock}
        </HBFlex>
    );

    const fieldRow = (
        <HBFlex
            direction={isColumn ? 'column' : 'row'}
            align={isColumn ? 'stretch' : 'center'}
        >
            {placement === 'right' ? (
                <>
                    {controlBlock}
                    {labelBlock || hiddenLabelSpacer}
                </>
            ) : (
                <>
                    {labelBlock || hiddenLabelSpacer}
                    {controlBlock}
                </>
            )}
        </HBFlex>
    );

    return (
        <FieldContainer
            className={className}
            $width={width}
            $margin={margin}
            $disabled={disabled}
        >
            {isMessageRow ? (
                <HBFlex direction="column">
                    {fieldRow}
                    {messageBlock}
                </HBFlex>
            ) : (
                fieldRow
            )}
        </FieldContainer>
    );
};

export default HBFieldControl;
